import { useState, FC } from 'react';
import {
  Contribution,
  ContributionClassesAndRolesDDL,
  ContributionClassName,
  ContributionsListItem,
} from 'types/contribution';
import { getChipRoleLabel } from 'app/components/Song/Contributions/ContributionsList/utils';
import { format } from 'date-fns';
import { dateFormats } from 'app/constants';
import { DeleteSongContributionDialog } from 'app/components/Song/Contributions/DeleteSongContributionDialog';
import { useAppSelector } from 'app/store';
import { getAffectsSplit } from 'app/store/SongList/selectors';
import { ListItemText, TableList } from 'app/components/ChakraTableList';
import {
  chakra,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Flex,
  Text,
  Button,
} from '@chakra-ui/react';
import { TruncatedChips } from 'app/components/Core/TruncatedChips';

interface ContributionsListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contributionItem: ContributionsListItem;
  contributionClassesAndRoles: ContributionClassesAndRolesDDL;
  deleteContributionAction: (guid: string) => void;
  contributionClassName: ContributionClassName;
  handleOpenEditDialog: (contributionGuid?: string) => void;
}

export const ContributionsListDialog: FC<ContributionsListDialogProps> = ({
  isOpen,
  onClose,
  contributionItem,
  contributionClassesAndRoles,
  deleteContributionAction,
  contributionClassName,
  handleOpenEditDialog,
}) => {
  const [selectedContributionGuid, setSelectedContributionGuid] = useState<
    string | undefined
  >();
  const affectsSplit = useAppSelector(getAffectsSplit(contributionClassName));

  return (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent data-testid="roles-list-dialog">
          <ModalHeader data-testid="dialog-title">
            <Flex flexDirection="column">
              <chakra.span
                textOverflow="ellipsis"
                overflow="hidden"
                title={contributionItem.profile?.displayName}
              >
                {`${contributionClassName} credits`}
              </chakra.span>
              <Text fontSize="xs" color="textSubdued">
                {contributionItem.profile?.displayName}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalBody>
            <TableList
              columns={[
                { key: 'role', name: 'Role' },
                { key: 'dateAdded', name: 'Date added' },
                { key: 'options', name: '', contextMenu: true },
              ]}
              onRowClick={(row) => {
                handleOpenEditDialog(row.rowData.contributionGuid);
              }}
              rows={contributionItem?.contributions?.map((contribution) => {
                const menuOptions = [
                  {
                    title: 'Edit',
                    value: 'EDIT',
                    onClick: () => {
                      handleOpenEditDialog(contribution.guid);
                    },
                  },
                  {
                    title: 'Delete',
                    value: 'DELETE',
                    onClick: () => {
                      if (contribution.guid)
                        setSelectedContributionGuid(contribution.guid);
                    },
                  },
                ];
                return {
                  rowData: {
                    options: menuOptions,
                    contributionGuid: contribution.guid,
                  },
                  clickable: true,
                  role: (
                    <TruncatedChips<Partial<Contribution>>
                      data-testid="credits-list-role"
                      items={[contribution]}
                      getKey={(contribution) =>
                        `contribution-${contribution.guid}`
                      }
                      getLabel={(contribution) =>
                        getChipRoleLabel(
                          contribution,
                          contributionClassesAndRoles.classRoles
                        )
                      }
                    />
                  ),
                  dateAdded: (
                    <ListItemText
                      data-testid="credits-list-date-added"
                      primary={
                        contribution?.date?.date
                          ? format(
                              new Date(contribution?.date?.date),
                              dateFormats.LIST_FORMAT
                            )
                          : '-'
                      }
                    />
                  ),
                };
              })}
            />
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <DeleteSongContributionDialog
        guid={selectedContributionGuid}
        onClose={() => setSelectedContributionGuid(undefined)}
        onConfirm={() => {
          if (selectedContributionGuid) {
            deleteContributionAction(selectedContributionGuid);
          }
        }}
        affectsSplit={affectsSplit}
      />
    </>
  );
};
